import { useCallback, useEffect, useRef, useState } from "react";

import {
  benchRecognizer,
  canRecordDictation,
  DictationTranscribeError,
  isRecognizerBenched,
  startDictationRecorder,
  transcribeDictationClip,
  type DictationRecorder,
} from "./dictationFallback.js";

/**
 * Voice-to-text (dictation) with TWO engines behind one hook (the embedded
 * Otto widget's dual-engine contract, ported 2026-07-16):
 *
 *  1. LIVE (preferred): the browser Web Speech API (`SpeechRecognition` /
 *     `webkitSpeechRecognition`) streams words while they're said. It runs
 *     entirely in the WebView and keeps audio off our servers — the browser
 *     talks to its own speech service and hands us back text.
 *  2. RECORDED (fallback): several real environments have a WORKING mic and
 *     a recognizer that silently yields nothing — the desktop app's macOS
 *     WKWebView (wry implements WebKit's media-capture permission delegate
 *     but NOT its separate speech-recognition one, so the recognizer never
 *     delivers), WebView2 / non-branded Chromium (no Google speech keys),
 *     and Firefox (no SpeechRecognition at all — it now gets the mic where
 *     it used to get none). While listening we therefore ALSO record the mic
 *     via `dictationFallback.ts`; if the session ends with ZERO recognized
 *     text, the clip is POSTed to `/api/otto/transcribe` (→ gateway Whisper)
 *     and the text lands exactly where live words would have gone. A
 *     recognition error while the mic itself works benches the recognizer
 *     for the rest of the page (the widget's sticky `serverMode`).
 *
 * THE PRIVACY PROPERTY: audio only leaves the machine when the platform
 *     recognizer produced nothing. A session with live text discards its
 *     recording; `cancel()` (send / voice-note takeover) always discards.
 *
 * The recognizer runs in continuous + interim mode: final phrases fire
 * `onResult` (the composer appends them), while the live, not-yet-final words
 * surface as `interim` for an "I'm listening" caption. Continuous recognition
 * still ends itself after a stretch of silence, so we transparently restart it
 * while the user still intends to be listening (`wantRef`).
 */

interface SpeechAlternative {
  readonly transcript: string;
}
interface SpeechRecognitionResultItem {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: SpeechAlternative | undefined;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  readonly [index: number]: SpeechRecognitionResultItem | undefined;
}
interface SpeechRecognitionResultEvent {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent {
  readonly error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Errors that mean "stop trying" rather than "silence, keep listening" —
 * only honoured when there is NO recorder to carry the session (with one, any
 * recognizer failure short of `audio-capture` just benches the recognizer). */
const FATAL_ERRORS = new Set(["not-allowed", "service-not-allowed", "audio-capture"]);
/** Benign recognizer noise — never bench on these. */
const BENIGN_ERRORS = new Set(["no-speech", "aborted"]);

/** One dictation session's fallback state. */
interface DictationSession {
  recorder: DictationRecorder | null;
  /** The live engine delivered at least one final phrase. */
  gotText: boolean;
  /** Send (or an error) said: drop the clip, whatever happens. */
  discard: boolean;
  /** The recognizer was benched mid-session; the recorder carries it now. */
  srDead: boolean;
  /** A LIVE recognizer reported `no-speech` (and no speech evidence arrived
   * after): the engine works and heard silence — nothing to rescue. */
  sawNoSpeech: boolean;
}

export interface SpeechToText {
  /** Whether dictation is available here (live engine OR recorded fallback). */
  readonly supported: boolean;
  /** Whether the recognizer/recorder is currently capturing. */
  readonly listening: boolean;
  /** Live, not-yet-finalized words (for an "I'm listening…" caption). */
  readonly interim: string;
  /** A recorded clip is at the server transcriber right now. */
  readonly transcribing: boolean;
  start(): void;
  /** End the session; a zero-text session transcribes its recording. */
  stop(): void;
  /** End the session and DROP the recording (send / voice-note takeover). */
  cancel(): void;
  toggle(): void;
}

export function useSpeechToText(opts: {
  /** Called with each finalized phrase (trimmed, non-empty). */
  onResult: (text: string) => void;
  /** Called with the raw error code on a fatal recognition error, or with
   * `transcribe-failed` / `transcribe-unavailable` when the fallback POST
   * fails. */
  onError?: (code: string) => void;
  /** BCP-47 language tag; defaults to en-US. */
  lang?: string;
}): SpeechToText {
  const ctorRef = useRef<SpeechRecognitionCtor | null>(null);
  if (ctorRef.current === null) ctorRef.current = getRecognitionCtor();
  const supported = ctorRef.current !== null || canRecordDictation();

  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [transcribing, setTranscribing] = useState(false);

  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const sessionRef = useRef<DictationSession | null>(null);
  const wantRef = useRef(false);
  const disposedRef = useRef(false);
  // Keep the latest callbacks without re-subscribing the recognizer.
  const onResultRef = useRef(opts.onResult);
  const onErrorRef = useRef(opts.onError);
  const langRef = useRef(opts.lang);
  onResultRef.current = opts.onResult;
  onErrorRef.current = opts.onError;
  langRef.current = opts.lang;

  /** The single way a session ends. Releasing the mic ALWAYS happens;
   * transcribing is conditional on the live engine having produced nothing. */
  const finishSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    setListening(false);
    setInterim("");
    if (!session?.recorder) return;
    const { recorder } = session;
    // Discard when the live engine won, a send said drop it, or a WORKING
    // recognizer affirmatively heard silence (posting a silent clip would
    // only make Whisper hallucinate — and audio should not leave the machine
    // when the live engine was demonstrably functional).
    if (session.discard || session.gotText || (session.sawNoSpeech && !session.srDead)) {
      recorder.discard();
      return;
    }
    void (async () => {
      const blob = await recorder.finish();
      if (!blob || disposedRef.current) return;
      // The live engine came back EMPTY: rescue the utterance we recorded.
      setTranscribing(true);
      try {
        const text = await transcribeDictationClip(blob);
        if (text && !disposedRef.current) onResultRef.current(text);
      } catch (err) {
        if (!disposedRef.current) {
          onErrorRef.current?.(
            err instanceof DictationTranscribeError ? err.code : "transcribe-failed",
          );
        }
      } finally {
        setTranscribing(false);
      }
    })();
  }, []);

  const start = useCallback(() => {
    if (recRef.current || sessionRef.current) return;
    const Ctor = ctorRef.current;
    const useRecognizer = Ctor !== null && !isRecognizerBenched();
    const recorder = startDictationRecorder(); // null where recording is unsupported
    if (!useRecognizer && !recorder) return; // no engine at all — no-op

    const session: DictationSession = {
      recorder,
      gotText: false,
      discard: false,
      srDead: false,
      sawNoSpeech: false,
    };
    sessionRef.current = session;
    wantRef.current = true;
    setInterim("");

    if (!useRecognizer) {
      // Recorded engine only: capture now, transcribe on stop.
      setListening(true);
      return;
    }

    const rec = new (Ctor as SpeechRecognitionCtor)();
    rec.lang = langRef.current ?? "en-US";
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const item = event.results[i];
        const transcript = item?.[0]?.transcript ?? "";
        if (!transcript) continue;
        if (item?.isFinal) finalText += transcript;
        else interimText += transcript;
      }
      const trimmed = finalText.trim();
      if (trimmed) {
        session.gotText = true;
        onResultRef.current(trimmed);
      }
      // Any speech evidence supersedes an earlier silence report.
      if (trimmed || interimText.trim()) session.sawNoSpeech = false;
      setInterim(interimText.trim());
    };

    rec.onerror = (event) => {
      if (BENIGN_ERRORS.has(event.error)) {
        if (event.error === "no-speech") session.sawNoSpeech = true;
        return; // onend decides
      }
      if (session.recorder && event.error !== "audio-capture") {
        // The RECOGNIZER failed, not the mic (WKWebView's speech-permission
        // `not-allowed`, keyless Chromium's instant `network`, …). Bench it
        // for the rest of the page and silently carry on recording — the
        // clip is transcribed on stop. That's exactly what the fallback is
        // for; no error surfaces to the user.
        benchRecognizer();
        session.srDead = true;
        return;
      }
      if (FATAL_ERRORS.has(event.error)) {
        wantRef.current = false;
        session.discard = true; // a broken/blocked mic has nothing to rescue
        onErrorRef.current?.(event.error);
      }
      // Non-fatal without a recorder falls through to onend, which restarts
      // while the user still intends to listen.
    };

    rec.onend = () => {
      setInterim("");
      if (wantRef.current && session.srDead) {
        // The benched recognizer wound down but the session lives on — the
        // recorder is carrying it until the user taps stop.
        recRef.current = null;
        return;
      }
      if (wantRef.current && recRef.current) {
        try {
          recRef.current.start();
          return;
        } catch {
          // Could not resume — fall through to a clean stop.
        }
      }
      recRef.current = null;
      finishSession();
    };

    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      // The recognizer refused to even start — record instead if we can.
      recRef.current = null;
      if (session.recorder) {
        benchRecognizer();
        session.srDead = true;
        setListening(true);
        return;
      }
      sessionRef.current = null;
      wantRef.current = false;
      setListening(false);
    }
  }, [finishSession]);

  const stop = useCallback(() => {
    wantRef.current = false;
    setInterim("");
    const session = sessionRef.current;
    const rec = recRef.current;
    if (rec && !session?.srDead) {
      try {
        rec.stop(); // → onend → finishSession
        setListening(false);
        return;
      } catch {
        // Already stopped — fall through to a direct finish.
      }
    }
    if (rec) {
      // A benched (dead) recognizer can't be trusted to fire onend — drop it.
      try {
        rec.abort();
      } catch {
        // ignore
      }
      recRef.current = null;
    }
    finishSession();
  }, [finishSession]);

  /** Sending mid-dictation means "send what's visible": drop the in-flight
   * recording rather than landing text into an already-sent composer. */
  const cancel = useCallback(() => {
    const session = sessionRef.current;
    if (session) session.discard = true;
    stop();
  }, [stop]);

  const toggle = useCallback(() => {
    if (recRef.current || sessionRef.current) stop();
    else start();
  }, [start, stop]);

  // Tear down on unmount — never leave the mic hot, never post the clip.
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      wantRef.current = false;
      try {
        recRef.current?.abort();
      } catch {
        // ignore
      }
      recRef.current = null;
      sessionRef.current?.recorder?.discard();
      sessionRef.current = null;
    };
  }, []);

  return { supported, listening, interim, transcribing, start, stop, cancel, toggle };
}
